import {useTranslation} from "react-i18next";
import Box from "@mui/material/Box/Box";
import Grid from "@mui/material/Grid/Grid";
import {useRecoilState} from "recoil";
import {useContext, useEffect, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import dayjs from "dayjs";
import {greenDiscountCreationAtom, initialGreenDiscountCreation,} from "../../../state/greenDiscount";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import useAgGridCustomHook from "../../../hooks/useAgGridCustomHook";
import {fromSingleGenericBeToSingleGenericFe, useActionColDef,} from "../../../utils";
import ActionsPopover from "../../../common/components/ActionsPopover";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../../constants";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import BasicToolbar from "../../../common/components/BasicToolbar";
import SectionHeader from "../../../../../util/component/SectionHeader";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import DialogComponent from "../../../common/components/DialogComponent";
import Loading from "../../../common/components/Loading";
import GreenDiscountFilters from "./GreenDiscountFilters";
import {GreenDiscountColDef} from "./GreenDiscountColDef";
import {
    FormGreenDiscountModel,
    FormSearchGreenDiscountModel,
    GreenDiscountEntityResponse,
} from "../../../api/types/greenDiscount";
import {useGreenDiscountRepository} from "../../../api/repositories/GreenDiscountRepository";
import GreenDiscountFormContainer from "../Form/GreenDiscountFormContainer";
import {useGreenDiscountDetailRepository} from "../../../api/repositories/GreenDiscountDetailRepository";

const UpsellingRate = () => {
    // Use Recoil
    const [creationForm, setCreationForm] = useRecoilState(
        greenDiscountCreationAtom
    );

    // Use State
    const [, setGreenDiscountUsed] = useState<boolean>(false)

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGridLocale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {
        getGreenDiscountPaged,
        deleteGreenDiscount,
        getGreenDiscountInUse,
        terminateGreenDiscount,
    } = useGreenDiscountRepository();
    const {getGreenDiscountMatrix} = useGreenDiscountDetailRepository();

    // Use State
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [isCreationFormOpen, setIsCreationFormOpen] = useState(false);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchGreenDiscountModel>({
        name: "",
        commercialSegmentId: null,
        commercialCategoryId: null,
        reservationFrom: "",
        reservationTo: "",
        pickupFrom: "",
        pickupTo: "",
        showOnlyActive: false,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            GreenDiscountEntityResponse,
            FormSearchGreenDiscountModel
        >({
            gridRef,
            api: getGreenDiscountPaged,
            initialSortColId: "name",
            entityFilters: testFilters,
            customSort: "GreenDiscountEntityResponse",
        });

    useEffect(() => {
        if (gridRef?.current)
            handleTriggerFilters({
                name: testFilters.name,
                commercialSegmentId:
                    (
                        testFilters.commercialSegmentId as SelectElementModelFE
                    )?.code?.toString() || "",
                commercialCategoryId:
                    (
                        testFilters.commercialCategoryId as SelectElementModelFE
                    )?.code?.toString() || "",
                reservationFrom: testFilters.reservationFrom
                    ? dayjs(testFilters.reservationFrom).toISOString()
                    : testFilters.reservationFrom,
                reservationTo: testFilters.reservationTo
                    ? dayjs(testFilters.reservationTo).toISOString()
                    : testFilters.reservationTo,
                pickupFrom: testFilters.pickupFrom
                    ? dayjs(testFilters.pickupFrom).toISOString()
                    : testFilters.pickupFrom,
                pickupTo: testFilters.pickupTo
                    ? dayjs(testFilters.pickupTo).toISOString()
                    : testFilters.pickupTo,
                showOnlyActive: testFilters.showOnlyActive,
                legalEntities:
                    ((testFilters.legalEntities as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? everyLegalEntitiesWithoutAll
                        : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
                dealers:
                    ((testFilters.dealers as SelectElementModelFE)
                        ?.code as string) === "all"
                        ? dealerFilteredOptions
                        : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
            });
    }, [testFilters.showOnlyActive]);

    const {actionColDef} = useActionColDef();
    const columnDefs: ColDef<GreenDiscountEntityResponse>[] = GreenDiscountColDef(
        {
            actionsRenderer: (params: ICellRendererParams) => (
                <ActionsPopover
                    params={params}
                    view={{
                        action: viewButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_VIEW_UPSELLING_RATE";
                            });
                        }),
                    }}
                    update={{
                        action: updateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_UPDATE_UPSELLING_RATE";
                            });
                        }),
                    }}
                    del={{
                        action: deleteButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_DELETE_UPSELLING_RATE";
                            });
                        }),
                    }}
                    terminate={{
                        action: terminateButtonClicked,
                        userHasAuth: !!user.profiles.find((profile: any) => {
                            return profile.auths.find((auth: any) => {
                                return auth.name === "SCOPE_PRC_TERMINATE_UPSELLING_RATE";
                            });
                        }),
                    }}
                />
            ),
            actionColDef,
            user
        }
    );

    const defaultColDef = DEFAULT_COL_DEF;

    /* TABLE ACTIONS - START */
    const viewButtonClicked = async (data: GreenDiscountEntityResponse) => {
        setCreationForm({
            readOnly: true,
            inUse: false,
            greenDiscountId: data.id,
            definitionData: getDefinitionData(data),
            matrixData: await greenDiscountMatrix(data.id),
            step: 0,
            completed: [true, true, true],
        });
        setIsCreationFormOpen(true);
    };

    const getDefinitionData = (
        data: GreenDiscountEntityResponse
    ): FormGreenDiscountModel => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegment: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const greenDiscountMatrix = async (
        groupId: GreenDiscountEntityResponse["id"]
    ) => {
        try {
            return await getGreenDiscountMatrix(groupId);
        } catch (e) {
        }
    };

    const updateButtonClicked = async (data: GreenDiscountEntityResponse) => {
        const definitionData = getDefinitionData(data);
        const matrixData = await greenDiscountMatrix(data.id);
        const greenDiscountInUse: boolean = await getGreenDiscountInUse({id: data.id});
        setGreenDiscountUsed(greenDiscountInUse)
        setCreationForm({
            readOnly: false,
            inUse: greenDiscountInUse,
            greenDiscountId: data.id,
            definitionData,
            matrixData,
            step: 0,
            completed: [
                !!definitionData,
                Boolean(matrixData?.length),
                Boolean(matrixData?.length)
            ]
        });
        setIsCreationFormOpen(true);
    };

    const terminateButtonClicked = async (data: GreenDiscountEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmTerminate(data.id),
            message: t("pricing:greenDiscount.terminate"),
        });
    };

    const handleConfirmTerminate = async (id: number) => {
        await terminateGreenDiscount(id);
        setDialog(null);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const deleteButtonClicked = (data: GreenDiscountEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:greenDiscount.remove"),
        });
    };

    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteGreenDiscount(id);
                setDialog(null);
                refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
        }
    };
    /* TABLE ACTIONS - END */

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        setTestFilters({
            name: "",
            commercialSegmentId: null,
            commercialCategoryId: null,
            reservationFrom: "",
            reservationTo: "",
            pickupFrom: "",
            pickupTo: "",
            showOnlyActive: false,
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            name: "",
            commercialSegmentId: null,
            commercialCategoryId: null,
            reservationFrom: "",
            reservationTo: "",
            pickupFrom: "",
            pickupTo: "",
            showOnlyActive: false,
            dealers: [],
            legalEntities: []
        });
    };

    const handleGetGreenDiscountList = () => {
        handleTriggerFilters({
            name: testFilters.name,
            commercialSegmentId:
                (
                    testFilters.commercialSegmentId as SelectElementModelFE
                )?.code?.toString() || "",
            commercialCategoryId:
                (
                    testFilters.commercialCategoryId as SelectElementModelFE
                )?.code?.toString() || "",
            reservationFrom: testFilters.reservationFrom
                ? dayjs(testFilters.reservationFrom).toISOString()
                : testFilters.reservationFrom,
            reservationTo: testFilters.reservationTo
                ? dayjs(testFilters.reservationTo).toISOString()
                : testFilters.reservationTo,
            pickupFrom: testFilters.pickupFrom
                ? dayjs(testFilters.pickupFrom).toISOString()
                : testFilters.pickupFrom,
            pickupTo: testFilters.pickupTo
                ? dayjs(testFilters.pickupTo).toISOString()
                : testFilters.pickupTo,
            showOnlyActive: testFilters.showOnlyActive,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : ((testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string)),
            dealers:
                ((testFilters.dealers as SelectElementModelFE)
                    ?.code as string) === "all"
                    ? dealerFilteredOptions
                    : ((testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string)),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleGetGreenDiscountList();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const onDone = () => {
        setIsCreationFormOpen(false);
        setCreationForm(initialGreenDiscountCreation);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    const onClose = () => {
        creationForm.readOnly || refresh();
        setIsCreationFormOpen(false);
        setCreationForm(initialGreenDiscountCreation);
    };

    const createButtonClicked = async () => {
        setCreationForm({
            readOnly: false,
            inUse: false,
            definitionData: undefined,
            step: 0,
            completed: [false, false, false],
        });
        setIsCreationFormOpen(true);
    };

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_UPSELLING_RATE";
                        });
                    })
                        ? {onClick: createButtonClicked}
                        : undefined
                }
                activeSwitch={{
                    label: t("pricing:rentalPeriods.form.labels.showActive"),
                    handleChange: (checked: boolean) => {
                        setTestFilters({
                            ...testFilters,
                            showOnlyActive: checked,
                        });
                    },
                    value: testFilters.showOnlyActive as boolean,
                }}
            />
        ),
    };

    return (
        <Box>
            <SectionHeader title={t("pricing:greenDiscount.header")}/>

            <GreenDiscountFormContainer
                onDone={onDone}
                isOpen={isCreationFormOpen}
                onClose={onClose}
                refresh={refresh}
            />

            <GreenDiscountFilters
                handleGetGreenDiscountList={handleGetGreenDiscountList}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={defaultColDef}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {dialog?.open && (
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    )}
                </Grid>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default UpsellingRate;
